"use client";

import {SearchParamsBoundary} from "@/components/navigation/SearchParamsBoundary";
import clsx from "clsx";
import {i18n, Locale} from "~/i18n-config";
import React, {Fragment, useEffect} from "react";
import {usePathname} from "next/navigation";
import {Menu,Transition} from "@headlessui/react";
import {LangLink} from "~/components/locales/LangLink";
import {getLanguageNameByLocale, removeLocaleFromPathname} from "~/utils/locales";

/**
 * LangNav
 *
 * The default language navigation
 *
 * @param currentLocale
 * @constructor
 */
function LangNavContent(
    { lang, light, searchParams } : { lang: Locale, light?: boolean, searchParams: URLSearchParams }
) {

    const pathname = usePathname();

    return (
        <Menu as="div" className="relative gap-4 text-center min-w-[180px]">
            {({ open, close }) => {

                /**
                 * Close popover after navigation
                 * whenever pathname changed
                 */
                useEffect(() => {
                    close();
                }, [pathname]);

                return (
                    <>
                        <Menu.Button className={clsx(
                            "inline-flex flex-row gap-2 items-center justify-center box-border rounded-md py-2 px-4 text-normal font-jost font-normal border-0 outline outline-offset-0 transition-all duration-300 capitalize",
                            light ? "outline-zinc-200 text-zinc-500" : "outline-white text-white",
                            open ? (light ? "outline-white/50" : "outline-white/50") : (light ? "bg-white outline-zinc-200/50 hover:outline-zinc-200" : "outline-white/50 hover:outline-white"),
                        )}>
                            {getLanguageNameByLocale(lang)}
                        </Menu.Button>
                        <Transition
                            as={Fragment}
                            enter="transition ease-out duration-100"
                            enterFrom="transform opacity-0 scale-95"
                            enterTo="transform opacity-100 scale-100"
                            leave="transition ease-in duration-75"
                            leaveFrom="transform opacity-100 scale-100"
                            leaveTo="transform opacity-0 scale-95">
                            <Menu.Items className={clsx(
                                "absolute left-0 right-0 bottom-full z-999 mb-4 w-full origin-bottom-right rounded-md shadow-lg outline outline-zinc-200 divide-y divide-zinc-100",
                                light ? "outline-zinc-200 outline-offset-0" : "outline-zinc-100 outline-offset-2"
                            )}>
                                {i18n.locales.map((langOption, index) => {
                                    if(langOption !== lang) {
                                        const urlSearchParams = new URLSearchParams(searchParams);
                                        urlSearchParams.set("locale", langOption);
                                        return (
                                            <Menu.Item key={`nav-lang-${langOption}`}>
                                                <LangLink key={`nav-lang-link-${langOption}`} lang={langOption} href={{
                                                    pathname: removeLocaleFromPathname(pathname),
                                                    query: urlSearchParams,
                                                }} className={clsx(
                                                    "first:rounded-t-md last:rounded-b-md w-full px-5 py-2 inline-flex items-center justify-center text-center text-normal font-jost transition-colors delay-100 hover:delay-0 capitalize",
                                                    light ? "bg-white text-zinc-700 hover:text-zinc-900 hover:bg-zinc-100" : "bg-white text-zinc-700 hover:text-zinc-900 hover:bg-zinc-100"
                                                )} prefetch={false}>
                                                    {getLanguageNameByLocale(langOption)}
                                                </LangLink>
                                            </Menu.Item>
                                        )
                                    }
                                })}
                            </Menu.Items>
                        </Transition>
                    </>
                )
            }}
        </Menu>
    );

}

function LangPageNavContent(
    { lang, page, searchParams }: { lang: Locale, page: any, searchParams: URLSearchParams }
) {
    const pathname = usePathname();

    return (
        <Menu as="div" className="relative gap-4 text-center min-w-[180px]">
            {({ open, close }) => {

                useEffect(() => {
                    close();
                }, [pathname]);

                return (
                    <>
                        <Menu.Button className={clsx(open ? "outline-white/50" : "outline-white/50 hover:outline-white", "outline-white text-white inline-flex flex-row gap-2 items-center justify-center box-border rounded-md py-2 px-4 text-normal font-jost font-normal border-0 outline outline-offset-0 transition-all duration-300 capitalize")}>
                            {getLanguageNameByLocale(lang)}
                        </Menu.Button>
                        <Transition
                            as={Fragment}
                            enter="transition ease-out duration-100"
                            enterFrom="transform opacity-0 scale-95"
                            enterTo="transform opacity-100 scale-100"
                            leave="transition ease-in duration-75"
                            leaveFrom="transform opacity-100 scale-100"
                            leaveTo="transform opacity-0 scale-95">
                            <Menu.Items className="absolute left-0 right-0 bottom-full z-999 mb-4 w-full origin-bottom-right rounded-md shadow-lg outline outline-zinc-200 divide-y divide-zinc-100">
                                {i18n.locales.map((locale, index) => {
                                    if (locale !== lang) {
                                        const urlSearchParams = new URLSearchParams(searchParams);
                                        urlSearchParams.set("locale", locale);
                                        return (
                                            <Menu.Item key={`lang-nav-${index}`}>
                                                <LangLink
                                                    key={`lang-nav-link-${index}`}
                                                    lang={locale}
                                                    href={{
                                                        pathname: page.slug[locale],
                                                        query: urlSearchParams,
                                                    }}
                                                    className="bg-white text-zinc-700 hover:text-zinc-900 hover:bg-zinc-100 first:rounded-t-md last:rounded-b-md w-full px-5 py-2 inline-flex items-center justify-center text-center text-normal font-jost transition-colors delay-100 hover:delay-0 capitalize"
                                                    prefetch={false}>
                                                    {getLanguageNameByLocale(locale)}
                                                </LangLink>
                                            </Menu.Item>
                                        )
                                    }
                                })}
                            </Menu.Items>
                        </Transition>
                    </>
                )
            }}
        </Menu>
    );
}
export function LangNav(props: Omit<React.ComponentProps<typeof LangNavContent>, "searchParams">) {
    return <SearchParamsBoundary>{searchParams => <LangNavContent {...props} searchParams={searchParams}/>}</SearchParamsBoundary>;
}
export function LangPageNav(props: Omit<React.ComponentProps<typeof LangPageNavContent>, "searchParams">) {
    return <SearchParamsBoundary>{searchParams => <LangPageNavContent {...props} searchParams={searchParams}/>}</SearchParamsBoundary>;
}
